import {
  collection,
  doc,
  addDoc,
  setDoc,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp
} from 'firebase/firestore';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { db, auth } from '../firebase/config';
import { deleteFile } from './cloudinary';

// Create new user manually
export const createUser = async (userData) => {
  try {
    // Create Firebase Auth user first
    const userCredential = await createUserWithEmailAndPassword(auth, userData.email, userData.password);
    const firebaseUser = userCredential.user;

    // Update display name
    const displayName = `${userData.firstName} ${userData.lastName}`;
    console.log('👤 Admin Service - Creating user with data:', userData);
    console.log('👤 Admin Service - Constructed displayName:', displayName);
    await updateProfile(firebaseUser, { displayName });
    console.log('👤 Admin Service - Firebase Auth profile updated with displayName:', displayName);

    // Create user profile in Firestore
    const user = {
      uid: firebaseUser.uid,
      firstName: userData.firstName,
      lastName: userData.lastName,
      displayName: `${userData.firstName} ${userData.lastName}`,
      email: userData.email,
      phone: userData.phone,
      levelId: userData.levelId,
      role: userData.role || 'student',
      subscription: {
        status: userData.subscriptionStatus || 'unpaid',
        planId: userData.levelId === 'grade7' ? 'grade7' : 'grade12',
        price: userData.levelId === 'grade7' ? 500 : 1000,
        currency: 'ZMW',
        startDate: userData.subscriptionStatus === 'active' ? serverTimestamp() : null,
        expiresAt: userData.subscriptionStatus === 'active' ? 
          new Date(new Date().setFullYear(new Date().getFullYear() + 1)) : null
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Store user in Firestore
    console.log('👤 Admin Service - Storing user in Firestore:', user);
    await setDoc(doc(db, 'users', firebaseUser.uid), user);
    console.log('👤 Admin Service - User successfully stored in Firestore');

    // Create subscription record
    const subscription = {
      userId: firebaseUser.uid,
      levelId: userData.levelId,
      status: userData.subscriptionStatus || 'unpaid',
      planId: userData.levelId === 'grade7' ? 'grade7' : 'grade12',
      price: userData.levelId === 'grade7' ? 500 : 1000,
      currency: 'ZMW',
      startDate: userData.subscriptionStatus === 'active' ? serverTimestamp() : null,
      expiresAt: userData.subscriptionStatus === 'active' ? 
        new Date(new Date().setFullYear(new Date().getFullYear() + 1)) : null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(doc(db, 'subscriptions', `${firebaseUser.uid}_${userData.levelId}`), subscription);

    return {
      id: firebaseUser.uid,
      ...user
    };
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

// Get admin dashboard statistics
export const getAdminStats = async () => {
  try {
    console.log('🔍 Attempting to fetch admin stats...');
    const stats = {};
    
    // Get user counts
    console.log('📊 Fetching users collection...');
    const usersSnapshot = await getDocs(collection(db, 'users'));
    console.log('✅ Users snapshot:', usersSnapshot.size, 'docs');
    stats.totalUsers = usersSnapshot.size;
    
    const activeUsersSnapshot = await getDocs(
      query(collection(db, 'users'), where('subscription.status', '==', 'active'))
    );
    stats.activeUsers = activeUsersSnapshot.size;
    
    // Get payment stats
    const paymentsSnapshot = await getDocs(collection(db, 'payments'));
    stats.totalPayments = paymentsSnapshot.size;
    
    const pendingPaymentsSnapshot = await getDocs(
      query(collection(db, 'payments'), where('status', '==', 'pending'))
    );
    stats.pendingPayments = pendingPaymentsSnapshot.size;
    
    // Get content stats
    const papersSnapshot = await getDocs(collection(db, 'papers'));
    stats.totalPapers = papersSnapshot.size;
    
    const videosSnapshot = await getDocs(collection(db, 'videos'));
    stats.totalVideos = videosSnapshot.size;
    
    const examsSnapshot = await getDocs(collection(db, 'exams'));
    stats.totalExams = examsSnapshot.size;
    
    // Get recent activity
    const recentUsersSnapshot = await getDocs(
      query(collection(db, 'users'), orderBy('createdAt', 'desc'), limit(5))
    );
    stats.recentUsers = recentUsersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    const recentPaymentsSnapshot = await getDocs(
      query(collection(db, 'payments'), orderBy('createdAt', 'desc'), limit(5))
    );
    stats.recentPayments = recentPaymentsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return stats;
  } catch (error) {
    console.error('❌ Error fetching admin stats:', error);
    console.error('🔍 Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    throw error;
  }
};

// User Management
export const getAllUsers = async (limitCount = 50) => {
  try {
    const usersSnapshot = await getDocs(
      query(collection(db, 'users'), orderBy('createdAt', 'desc'), limit(limitCount))
    );
    return usersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const updateUserSubscription = async (userId, subscriptionData) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      subscription: subscriptionData,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating subscription:', error);
    throw error;
  }
};

export const deleteUser = async (userId) => {
  try {
    await deleteDoc(doc(db, 'users', userId));
    return true;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

// Content Management - Past Papers
export const uploadPastPaper = async (paperData) => {
  try {
    const paper = {
      title: paperData.title,
      description: paperData.description,
      subjectId: paperData.subjectId,
      levelId: paperData.levelId,
      year: paperData.year,
      term: paperData.term,
      paperType: paperData.paperType, // 'past_paper', 'mock_exam', 'revision'
      fileUrl: paperData.fileUrl,
      fileName: paperData.fileName,
      fileSize: paperData.fileSize,
      downloadCount: 0,
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'papers'), paper);
    return {
      id: docRef.id,
      ...paper
    };
  } catch (error) {
    console.error('Error uploading past paper:', error);
    throw error;
  }
};

export const updatePastPaper = async (paperId, paperData) => {
  try {
    const paperRef = doc(db, 'papers', paperId);
    await updateDoc(paperRef, {
      ...paperData,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating past paper:', error);
    throw error;
  }
};

export const deletePastPaper = async (paperId) => {
  try {
    // Get the paper data first to retrieve the file URL
    const paperDoc = await getDoc(doc(db, 'papers', paperId));
    if (!paperDoc.exists()) {
      throw new Error('Paper not found');
    }
    
    const paperData = paperDoc.data();
    
    // Delete file from Cloudinary if it exists
    if (paperData.fileName) {
      try {
        // Use 'raw' resource type for PDFs
        await deleteFile(paperData.fileName, 'raw');
      } catch (cloudinaryError) {
        console.warn('Failed to delete file from Cloudinary:', cloudinaryError);
        // Continue with database deletion even if Cloudinary deletion fails
      }
    }
    
    // Delete the paper document from Firestore
    await deleteDoc(doc(db, 'papers', paperId));
    return true;
  } catch (error) {
    console.error('Error deleting past paper:', error);
    throw error;
  }
};

export const getAllPapers = async (levelId = null, subjectId = null) => {
  try {
    let q = query(collection(db, 'papers'), orderBy('createdAt', 'desc'));
    
    if (levelId) {
      q = query(q, where('levelId', '==', levelId));
    }
    
    if (subjectId) {
      q = query(q, where('subjectId', '==', subjectId));
    }
    
    const papersSnapshot = await getDocs(q);
    return papersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching papers:', error);
    throw error;
  }
};

// Content Management - Video Lessons
export const addVideoLesson = async (videoData) => {
  try {
    const video = {
      title: videoData.title,
      description: videoData.description,
      subjectId: videoData.subjectId,
      levelId: videoData.levelId,
      videoUrl: videoData.videoUrl,
      thumbnailUrl: videoData.thumbnailUrl,
      duration: videoData.duration,
      topic: videoData.topic,
      isActive: true,
      viewCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'videos'), video);
    return {
      id: docRef.id,
      ...video
    };
  } catch (error) {
    console.error('Error adding video lesson:', error);
    throw error;
  }
};

export const updateVideoLesson = async (videoId, videoData) => {
  try {
    const videoRef = doc(db, 'videos', videoId);
    await updateDoc(videoRef, {
      ...videoData,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating video lesson:', error);
    throw error;
  }
};

export const deleteVideoLesson = async (videoId) => {
  try {
    await deleteDoc(doc(db, 'videos', videoId));
    return true;
  } catch (error) {
    console.error('Error deleting video lesson:', error);
    throw error;
  }
};

export const getAllVideos = async (levelId = null, subjectId = null) => {
  try {
    let q = query(collection(db, 'videos'), orderBy('createdAt', 'desc'));
    
    if (levelId) {
      q = query(q, where('levelId', '==', levelId));
    }
    
    if (subjectId) {
      q = query(q, where('subjectId', '==', subjectId));
    }
    
    const videosSnapshot = await getDocs(q);
    return videosSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching videos:', error);
    throw error;
  }
};

// Exam Management
export const createExam = async (examData) => {
  try {
    const exam = {
      title: examData.title,
      description: examData.description,
      subjectId: examData.subjectId,
      levelId: examData.levelId,
      duration: examData.duration, // in minutes
      totalMarks: examData.totalMarks,
      passingMarks: examData.passingMarks,
      questions: examData.questions,
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'exams'), exam);
    return {
      id: docRef.id,
      ...exam
    };
  } catch (error) {
    console.error('Error creating exam:', error);
    throw error;
  }
};

export const updateExam = async (examId, examData) => {
  try {
    const examRef = doc(db, 'exams', examId);
    await updateDoc(examRef, {
      ...examData,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating exam:', error);
    throw error;
  }
};

export const deleteExam = async (examId) => {
  try {
    await deleteDoc(doc(db, 'exams', examId));
    return true;
  } catch (error) {
    console.error('Error deleting exam:', error);
    throw error;
  }
};

export const getAllExams = async (levelId = null, subjectId = null) => {
  try {
    let q = query(collection(db, 'exams'), orderBy('createdAt', 'desc'));
    
    if (levelId) {
      q = query(q, where('levelId', '==', levelId));
    }
    
    if (subjectId) {
      q = query(q, where('subjectId', '==', subjectId));
    }
    
    const examsSnapshot = await getDocs(q);
    return examsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching exams:', error);
    throw error;
  }
};

// Subject Management
export const getAllSubjects = async () => {
  try {
    const subjectsSnapshot = await getDocs(collection(db, 'subjects'));
    return subjectsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching subjects:', error);
    throw error;
  }
};

export const addSubject = async (subjectData) => {
  try {
    const subject = {
      name: subjectData.name,
      description: subjectData.description,
      levelId: subjectData.levelId,
      icon: subjectData.icon,
      color: subjectData.color,
      isActive: true,
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'subjects'), subject);
    return {
      id: docRef.id,
      ...subject
    };
  } catch (error) {
    console.error('Error adding subject:', error);
    throw error;
  }
};

export const updateSubject = async (subjectId, subjectData) => {
  try {
    const subjectRef = doc(db, 'subjects', subjectId);
    await updateDoc(subjectRef, {
      ...subjectData,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating subject:', error);
    throw error;
  }
};

export const deleteSubject = async (subjectId) => {
  try {
    await deleteDoc(doc(db, 'subjects', subjectId));
    return true;
  } catch (error) {
    console.error('Error deleting subject:', error);
    throw error;
  }
};

// Analytics
export const getPaymentAnalytics = async (startDate = null, endDate = null) => {
  try {
    let q = query(collection(db, 'payments'), orderBy('createdAt', 'desc'));
    
    if (startDate) {
      q = query(q, where('createdAt', '>=', startDate));
    }
    
    if (endDate) {
      q = query(q, where('createdAt', '<=', endDate));
    }
    
    const paymentsSnapshot = await getDocs(q);
    const payments = paymentsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Calculate analytics
    const totalRevenue = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
    const approvedPayments = payments.filter(p => p.status === 'approved');
    const pendingPayments = payments.filter(p => p.status === 'pending');
    const rejectedPayments = payments.filter(p => p.status === 'rejected');
    
    return {
      totalPayments: payments.length,
      totalRevenue,
      approvedPayments: approvedPayments.length,
      pendingPayments: pendingPayments.length,
      rejectedPayments: rejectedPayments.length,
      payments
    };
  } catch (error) {
    console.error('Error fetching payment analytics:', error);
    throw error;
  }
};

export const getUserAnalytics = async () => {
  try {
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const users = usersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Calculate analytics
    const activeSubscriptions = users.filter(u => u.subscription?.status === 'active');
    const pendingPayments = users.filter(u => u.subscription?.status === 'payment_pending');
    const unpaidUsers = users.filter(u => u.subscription?.status === 'unpaid');
    const expiredUsers = users.filter(u => u.subscription?.status === 'expired');
    
    // Group by level
    const levelCounts = users.reduce((acc, user) => {
      const level = user.levelId || 'unknown';
      acc[level] = (acc[level] || 0) + 1;
      return acc;
    }, {});
    
    return {
      totalUsers: users.length,
      activeSubscriptions: activeSubscriptions.length,
      pendingPayments: pendingPayments.length,
      unpaidUsers: unpaidUsers.length,
      expiredUsers: expiredUsers.length,
      levelCounts,
      users
    };
  } catch (error) {
    console.error('Error fetching user analytics:', error);
    throw error;
  }
};

const adminServices = {
  getAdminStats,
  getAllUsers,
  updateUserSubscription,
  deleteUser,
  uploadPastPaper,
  updatePastPaper,
  deletePastPaper,
  getAllPapers,
  addVideoLesson,
  updateVideoLesson,
  deleteVideoLesson,
  getAllVideos,
  createExam,
  updateExam,
  deleteExam,
  getAllExams,
  getAllSubjects,
  addSubject,
  updateSubject,
  deleteSubject,
  getPaymentAnalytics,
  getUserAnalytics
};

export default adminServices;
